import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeadQueue, getLeadStats, getPricingRules, updatePricingRules, getIcpConfig, updateIcpConfig, getTaskHistory, runTaskManually, getAgentConfig } from '../lib/api';
import { toast } from 'sonner';

const T = {
  pl: {
    title: 'Kolejka leadow i priorytetyzacja',
    stats: 'Statystyki',
    hot: 'Gorace',
    warm: 'Cieple',
    cool: 'Chlodne',
    cold: 'Zimne',
    avgScore: 'Sredni wynik',
    needsContact: 'Do kontaktu',
    needsFollowup: 'Follow-up',
    needsResearch: 'Do zbadania',
    contractor: 'Kontrahent',
    company: 'Firma',
    score: 'Wynik',
    priority: 'Priorytet',
    action: 'Akcja',
    activeNegs: 'Aktywne neg.',
    lastActivity: 'Ostatnia aktywnosc',
    contactNow: 'Skontaktuj sie',
    followUp: 'Follow-up',
    research: 'Zbadaj',
    nurture: 'Pielegnuj',
    pricingRules: 'Reguly cenowe',
    minMargin: 'Min. marza (%)',
    maxDiscount: 'Maks. rabat (%)',
    paymentTerms: 'Termin platnosci',
    volumeDiscounts: 'Rabaty ilosciowe',
    addVolumeDiscount: 'Dodaj prog',
    minQty: 'Min. ilosc',
    discountPct: 'Rabat %',
    pricingNotes: 'Uwagi cenowe',
    icpConfig: 'Profil idealnego klienta (ICP)',
    industries: 'Branze',
    regions: 'Regiony',
    requiredTags: 'Wymagane tagi',
    icpNotes: 'Uwagi ICP',
    save: 'Zapisz',
    saved: 'Zapisano',
    taskScheduler: 'Harmonogram zadan',
    taskHistory: 'Historia zadan',
    runNow: 'Uruchom teraz',
    noTasks: 'Brak skonfigurowanych zadan',
    breakdown: 'Rozbicie wyniku',
    recency: 'Aktualnosc',
    frequency: 'Czestotliwosc',
    dealValue: 'Wartosc dealu',
    negProgress: 'Postep negocjacji',
    profileComp: 'Kompletnosc profilu',
    reputation: 'Reputacja',
    icpMatch: 'Dopasowanie ICP',
    loading: 'Ladowanie...',
    all: 'Wszystkie',
  },
  en: {
    title: 'Lead Queue & Prioritization',
    stats: 'Statistics',
    hot: 'Hot',
    warm: 'Warm',
    cool: 'Cool',
    cold: 'Cold',
    avgScore: 'Avg Score',
    needsContact: 'Needs Contact',
    needsFollowup: 'Follow-up',
    needsResearch: 'Needs Research',
    contractor: 'Contractor',
    company: 'Company',
    score: 'Score',
    priority: 'Priority',
    action: 'Action',
    activeNegs: 'Active Neg.',
    lastActivity: 'Last Activity',
    contactNow: 'Contact Now',
    followUp: 'Follow-up',
    research: 'Research',
    nurture: 'Nurture',
    pricingRules: 'Pricing Rules',
    minMargin: 'Min Margin (%)',
    maxDiscount: 'Max Discount (%)',
    paymentTerms: 'Payment Terms',
    volumeDiscounts: 'Volume Discounts',
    addVolumeDiscount: 'Add Tier',
    minQty: 'Min Qty',
    discountPct: 'Discount %',
    pricingNotes: 'Pricing Notes',
    icpConfig: 'Ideal Customer Profile (ICP)',
    industries: 'Industries',
    regions: 'Regions',
    requiredTags: 'Required Tags',
    icpNotes: 'ICP Notes',
    save: 'Save',
    saved: 'Saved',
    taskScheduler: 'Task Scheduler',
    taskHistory: 'Task History',
    runNow: 'Run Now',
    noTasks: 'No tasks configured',
    breakdown: 'Score Breakdown',
    recency: 'Recency',
    frequency: 'Frequency',
    dealValue: 'Deal Value',
    negProgress: 'Neg. Progress',
    profileComp: 'Profile Completeness',
    reputation: 'Reputation',
    icpMatch: 'ICP Match',
    loading: 'Loading...',
    all: 'All',
  }
};

const PRIORITY_COLORS = {
  hot: 'bg-red-100 text-red-800 border-red-200',
  warm: 'bg-orange-100 text-orange-800 border-orange-200',
  cool: 'bg-blue-100 text-blue-800 border-blue-200',
  cold: 'bg-gray-100 text-gray-600 border-gray-200',
};

const ACTION_ICONS = {
  contact_now: '📞',
  follow_up: '🔄',
  research_needed: '🔍',
  nurture: '🌱',
};

export default function LeadQueue({ lang = 'pl', tenantId }) {
  const t = T[lang] || T.pl;
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  // Pricing Rules
  const [pricing, setPricing] = useState({ min_margin_pct: 10, max_discount_pct: 15, volume_discounts: [], payment_terms: '30 dni', notes: '' });
  const [pricingDirty, setPricingDirty] = useState(false);

  // ICP Config
  const [icp, setIcp] = useState({ industries: [], regions: [], required_tags: [], notes: '' });
  const [icpDirty, setIcpDirty] = useState(false);
  const [icpInput, setIcpInput] = useState({ industries: '', regions: '', required_tags: '' });

  // Tasks
  const [tasks, setTasks] = useState([]);
  const [taskHistory, setTaskHistory] = useState([]);
  const [runningTask, setRunningTask] = useState(null);

  // Active tab
  const [tab, setTab] = useState('queue');

  useEffect(() => { loadAll(); }, [tenantId]);

  async function loadAll() {
    setLoading(true);
    try {
      const [q, s, p, ic, cfg] = await Promise.all([
        getLeadQueue(tenantId, 50),
        getLeadStats(tenantId),
        getPricingRules(tenantId),
        getIcpConfig(tenantId),
        getAgentConfig(tenantId),
      ]);
      setQueue(q);
      setStats(s);
      if (p) setPricing(p);
      if (ic) {
        setIcp(ic);
        setIcpInput({
          industries: (ic.industries || []).join(', '),
          regions: (ic.regions || []).join(', '),
          required_tags: (ic.required_tags || []).join(', '),
        });
      }
      if (cfg?.tasks) setTasks(cfg.tasks);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function loadTaskHistory() {
    try {
      const h = await getTaskHistory(tenantId);
      setTaskHistory(h);
    } catch (err) { console.error(err); }
  }

  async function savePricing() {
    try {
      await updatePricingRules(pricing, tenantId);
      setPricingDirty(false);
      toast.success(t.saved);
    } catch (err) { toast.error('Error saving pricing rules'); }
  }

  async function saveIcp() {
    const data = {
      ...icp,
      industries: icpInput.industries.split(',').map(s => s.trim()).filter(Boolean),
      regions: icpInput.regions.split(',').map(s => s.trim()).filter(Boolean),
      required_tags: icpInput.required_tags.split(',').map(s => s.trim()).filter(Boolean),
    };
    try {
      await updateIcpConfig(data, tenantId);
      setIcp(data);
      setIcpDirty(false);
      toast.success(t.saved);
      // Reload queue with new ICP
      const q = await getLeadQueue(tenantId, 50);
      setQueue(q);
    } catch (err) { toast.error('Error saving ICP config'); }
  }

  async function handleRunTask(taskId) {
    setRunningTask(taskId);
    try {
      const result = await runTaskManually(taskId, tenantId);
      toast.success(result.result || 'Task completed');
      loadAll();
    } catch (err) { toast.error('Task failed'); }
    setRunningTask(null);
  }

  const filtered = filter === 'all' ? queue : queue.filter(c => c.priority === filter);

  if (loading) return <div className="p-8 text-center text-muted-foreground">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t.title}</h1>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {['queue', 'pricing', 'icp', 'tasks'].map(tb => (
          <button key={tb} onClick={() => { setTab(tb); if (tb === 'tasks') loadTaskHistory(); }}
            className={`px-4 py-2 rounded-t text-sm font-medium transition-colors ${tab === tb ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
            {tb === 'queue' ? t.title.split(' ')[0] : tb === 'pricing' ? t.pricingRules : tb === 'icp' ? 'ICP' : t.taskScheduler}
          </button>
        ))}
      </div>

      {/* TAB: Lead Queue */}
      {tab === 'queue' && (
        <>
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {[
                { label: t.hot, value: stats.hot, cls: 'text-red-600' },
                { label: t.warm, value: stats.warm, cls: 'text-orange-600' },
                { label: t.cool, value: stats.cool, cls: 'text-blue-600' },
                { label: t.cold, value: stats.cold, cls: 'text-gray-500' },
                { label: t.avgScore, value: stats.avg_score, cls: 'text-primary' },
                { label: t.needsContact, value: stats.needs_contact, cls: 'text-red-600' },
                { label: t.needsFollowup, value: stats.needs_followup, cls: 'text-orange-600' },
                { label: t.needsResearch, value: stats.needs_research, cls: 'text-blue-600' },
              ].map((s, i) => (
                <div key={i} className="bg-card rounded-lg border p-3 text-center">
                  <div className={`text-xl font-bold ${s.cls}`}>{s.value}</div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Filter */}
          <div className="flex gap-2">
            {['all', 'hot', 'warm', 'cool', 'cold'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                {f === 'all' ? t.all : t[f]} {f !== 'all' && stats ? `(${stats[f]})` : ''}
              </button>
            ))}
          </div>

          {/* Queue Table */}
          <div className="bg-card rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-3 text-left">{t.contractor}</th>
                  <th className="p-3 text-left">{t.company}</th>
                  <th className="p-3 text-center">{t.score}</th>
                  <th className="p-3 text-center">{t.priority}</th>
                  <th className="p-3 text-center">{t.action}</th>
                  <th className="p-3 text-center">{t.activeNegs}</th>
                  <th className="p-3 text-left">{t.lastActivity}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(lead => (
                  <React.Fragment key={lead.contractor_id}>
                    <tr className="border-b hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => setExpandedId(expandedId === lead.contractor_id ? null : lead.contractor_id)}>
                      <td className="p-3 font-medium">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/contractors/${lead.contractor_id}`); }}
                          className="hover:text-primary hover:underline">{lead.contractor_name}</button>
                      </td>
                      <td className="p-3 text-muted-foreground">{lead.company}</td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{
                              width: `${lead.lead_score}%`,
                              backgroundColor: lead.lead_score >= 75 ? '#ef4444' : lead.lead_score >= 55 ? '#f97316' : lead.lead_score >= 35 ? '#3b82f6' : '#9ca3af'
                            }} />
                          </div>
                          <span className="font-bold text-sm">{lead.lead_score}</span>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${PRIORITY_COLORS[lead.priority]}`}>
                          {t[lead.priority]}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-xs">{ACTION_ICONS[lead.action]} {t[lead.action === 'contact_now' ? 'contactNow' : lead.action === 'follow_up' ? 'followUp' : lead.action === 'research_needed' ? 'research' : 'nurture']}</span>
                      </td>
                      <td className="p-3 text-center font-medium">{lead.active_negotiations}</td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {lead.last_activity ? new Date(lead.last_activity).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                    {expandedId === lead.contractor_id && (
                      <tr className="border-b bg-muted/20">
                        <td colSpan={7} className="p-4">
                          <div className="text-xs font-medium mb-2">{t.breakdown}</div>
                          <div className="grid grid-cols-7 gap-2">
                            {Object.entries(lead.breakdown || {}).map(([key, val]) => (
                              <div key={key} className="text-center">
                                <div className="h-16 flex items-end justify-center mb-1">
                                  <div className="w-6 bg-primary/30 rounded-t" style={{ height: `${val * 100}%` }}>
                                    <div className="w-full bg-primary rounded-t" style={{ height: `${val * 100}%` }} />
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground">{t[key] || key}</div>
                                <div className="text-xs font-medium">{Math.round(val * 100)}%</div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Brak kontrahentow</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* TAB: Pricing Rules */}
      {tab === 'pricing' && (
        <div className="bg-card rounded-xl border p-6 space-y-4 max-w-2xl">
          <h2 className="text-lg font-semibold">{t.pricingRules}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">{t.minMargin}</label>
              <input type="number" value={pricing.min_margin_pct || ''} onChange={e => { setPricing({ ...pricing, min_margin_pct: parseFloat(e.target.value) || 0 }); setPricingDirty(true); }}
                className="w-full mt-1 rounded border px-3 py-2 bg-background" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">{t.maxDiscount}</label>
              <input type="number" value={pricing.max_discount_pct || ''} onChange={e => { setPricing({ ...pricing, max_discount_pct: parseFloat(e.target.value) || 0 }); setPricingDirty(true); }}
                className="w-full mt-1 rounded border px-3 py-2 bg-background" />
            </div>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">{t.paymentTerms}</label>
            <input value={pricing.payment_terms || ''} onChange={e => { setPricing({ ...pricing, payment_terms: e.target.value }); setPricingDirty(true); }}
              className="w-full mt-1 rounded border px-3 py-2 bg-background" />
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm text-muted-foreground">{t.volumeDiscounts}</label>
              <button onClick={() => { setPricing({ ...pricing, volume_discounts: [...(pricing.volume_discounts || []), { min_qty: 0, discount_pct: 0 }] }); setPricingDirty(true); }}
                className="text-xs text-primary hover:underline">+ {t.addVolumeDiscount}</button>
            </div>
            {(pricing.volume_discounts || []).map((vd, i) => (
              <div key={i} className="flex gap-2 mb-2 items-center">
                <input type="number" placeholder={t.minQty} value={vd.min_qty || ''} onChange={e => {
                  const updated = [...pricing.volume_discounts]; updated[i] = { ...vd, min_qty: parseFloat(e.target.value) || 0 };
                  setPricing({ ...pricing, volume_discounts: updated }); setPricingDirty(true);
                }} className="w-32 rounded border px-2 py-1.5 text-sm bg-background" />
                <input type="number" placeholder={t.discountPct} value={vd.discount_pct || ''} onChange={e => {
                  const updated = [...pricing.volume_discounts]; updated[i] = { ...vd, discount_pct: parseFloat(e.target.value) || 0 };
                  setPricing({ ...pricing, volume_discounts: updated }); setPricingDirty(true);
                }} className="w-24 rounded border px-2 py-1.5 text-sm bg-background" />
                <span className="text-xs text-muted-foreground">%</span>
                <button onClick={() => {
                  const updated = pricing.volume_discounts.filter((_, j) => j !== i);
                  setPricing({ ...pricing, volume_discounts: updated }); setPricingDirty(true);
                }} className="text-red-400 hover:text-red-600 text-sm px-1">x</button>
              </div>
            ))}
          </div>
          <div>
            <label className="text-sm text-muted-foreground">{t.pricingNotes}</label>
            <textarea value={pricing.notes || ''} onChange={e => { setPricing({ ...pricing, notes: e.target.value }); setPricingDirty(true); }}
              className="w-full mt-1 rounded border px-3 py-2 bg-background text-sm" rows={3} />
          </div>
          {pricingDirty && (
            <button onClick={savePricing} className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 text-sm font-medium">
              {t.save}
            </button>
          )}
        </div>
      )}

      {/* TAB: ICP Config */}
      {tab === 'icp' && (
        <div className="bg-card rounded-xl border p-6 space-y-4 max-w-2xl">
          <h2 className="text-lg font-semibold">{t.icpConfig}</h2>
          <p className="text-sm text-muted-foreground">Zdefiniuj profil idealnego klienta. Kontrahenci pasujacy do profilu otrzymaja wyzszy lead score.</p>
          <div>
            <label className="text-sm text-muted-foreground">{t.industries} (oddzielone przecinkiem)</label>
            <input value={icpInput.industries} onChange={e => { setIcpInput({ ...icpInput, industries: e.target.value }); setIcpDirty(true); }}
              placeholder="np. budownictwo, przemysl, handel" className="w-full mt-1 rounded border px-3 py-2 bg-background text-sm" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">{t.regions} (oddzielone przecinkiem)</label>
            <input value={icpInput.regions} onChange={e => { setIcpInput({ ...icpInput, regions: e.target.value }); setIcpDirty(true); }}
              placeholder="np. Warszawa, Krakow, Slask" className="w-full mt-1 rounded border px-3 py-2 bg-background text-sm" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">{t.requiredTags} (oddzielone przecinkiem)</label>
            <input value={icpInput.required_tags} onChange={e => { setIcpInput({ ...icpInput, required_tags: e.target.value }); setIcpDirty(true); }}
              placeholder="np. B2B, dystrybutor, premium" className="w-full mt-1 rounded border px-3 py-2 bg-background text-sm" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">{t.icpNotes}</label>
            <textarea value={icp.notes || ''} onChange={e => { setIcp({ ...icp, notes: e.target.value }); setIcpDirty(true); }}
              className="w-full mt-1 rounded border px-3 py-2 bg-background text-sm" rows={3} />
          </div>
          {icpDirty && (
            <button onClick={saveIcp} className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 text-sm font-medium">
              {t.save}
            </button>
          )}
        </div>
      )}

      {/* TAB: Task Scheduler */}
      {tab === 'tasks' && (
        <div className="space-y-4">
          <div className="bg-card rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">{t.taskScheduler}</h2>
            {tasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t.noTasks}. Dodaj zadania w konfiguracji agenta.</p>
            ) : (
              <div className="space-y-2">
                {tasks.map(task => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                    <div>
                      <div className="font-medium text-sm">{task.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {task.category} | {task.days?.join(', ') || 'codziennie'} | {task.time_from || '00:00'}-{task.time_to || '23:59'}
                        {!task.active && <span className="ml-2 text-yellow-600">(nieaktywne)</span>}
                      </div>
                    </div>
                    <button onClick={() => handleRunTask(task.id)} disabled={runningTask === task.id}
                      className="px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50">
                      {runningTask === task.id ? '...' : t.runNow}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-card rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">{t.taskHistory}</h2>
            {taskHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground">Brak historii zadan</p>
            ) : (
              <div className="space-y-2">
                {taskHistory.slice(0, 20).map((h, i) => (
                  <div key={i} className="flex items-center justify-between p-2 border-b last:border-0">
                    <div>
                      <span className="text-sm font-medium">{h.task_title}</span>
                      <span className="text-xs text-muted-foreground ml-2">{h.category}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${h.result?.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {h.result?.status}
                      </span>
                      <span className="text-xs text-muted-foreground">{h.executed_at ? new Date(h.executed_at).toLocaleString() : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
